import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Ban } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tranches whose balance is a sub-R1 remainder (user 2026-10-19). Every
 * screen already shows them as R0; marking them Inactive stops them
 * accruing interest. Backend: GET /admin/data-alerts/leftovers,
 * POST /admin/data-alerts/leftovers/mark-inactive (audited).
 */
export default function LeftoverTranchesCard({ token, hideWhenEmpty = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const headers = { Authorization: `Bearer ${token}` };
  const keyOf = (i) => `${i.side}|${i.loan_id}|${i.risk_participant || ''}`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/data-alerts/leftovers`, { headers });
      setItems(r.data?.items || []);
      setPicked(new Set((r.data?.items || []).map(keyOf)));
    } catch (e) {
      toast.error('Could not load leftover balances');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const markInactive = async () => {
    const chosen = items.filter((i) => picked.has(keyOf(i)));
    if (!chosen.length) return;
    if (!window.confirm(`Mark ${chosen.length} tranche(s) Inactive? They stop accruing interest. Each change is recorded in the Audit Trail.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/admin/data-alerts/leftovers/mark-inactive`,
        { items: chosen.map((i) => ({ side: i.side, loan_id: i.loan_id, risk_participant: i.risk_participant })) }, { headers });
      toast.success(`Marked ${r.data?.marked || 0} tranche(s) Inactive`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark them inactive');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (k) => setPicked((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  if (hideWhenEmpty && (loading || items.length === 0)) return null;

  return (
    <Card data-testid="leftover-tranches-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Leftover balances under R1 {!loading && <span className="text-sm font-normal text-slate-500">({items.length})</span>}
        </CardTitle>
        <CardDescription>
          Repaid tranches whose cashflows leave a few cents. They already show as R0 everywhere; mark them Inactive so they
          stop accruing interest.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">None — every tranche with a balance has at least R1.</div>
        ) : (
          <>
            <div className="max-h-72 overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-1 w-8"></th>
                    <th className="px-2 py-1 text-left">Side</th>
                    <th className="px-2 py-1 text-left">Loan</th>
                    <th className="px-2 py-1 text-left">Investor / client</th>
                    <th className="px-2 py-1 text-left">Stage</th>
                    <th className="px-2 py-1 text-left">Last cashflow</th>
                    <th className="px-2 py-1 text-right">Leftover</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((i) => (
                    <tr key={keyOf(i)}>
                      <td className="px-2 py-1"><input type="checkbox" className="h-4 w-4" checked={picked.has(keyOf(i))} onChange={() => toggle(keyOf(i))} /></td>
                      <td className="px-2 py-1">{i.side === 'RP' ? 'Investor' : 'Client'}</td>
                      <td className="px-2 py-1 font-mono">{i.loan_id}</td>
                      <td className="px-2 py-1 font-mono">{i.side === 'RP' ? i.risk_participant : i.client_id}</td>
                      <td className="px-2 py-1">{i.stage}</td>
                      <td className="px-2 py-1 font-mono text-xs">{i.last_cashflow || '—'}</td>
                      <td className="px-2 py-1 text-right font-mono">R {Number(i.balance).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button onClick={markInactive} disabled={busy || picked.size === 0} data-testid="leftovers-mark-inactive">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
                Mark {picked.size} Inactive
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
